<template>
  <a-spin :spinning="spinning">
    <div :id="chartId" :style="{ minHeight: height+'px'}"></div>
  </a-spin>
</template>

<script>
import moment from 'moment'
import { Chart } from '@antv/g2'
import { humanizeValue } from '@/utils'

const chart = {}

const colors = ['#096dd9', '#00d1ed', '#ffc75f']

export default {
  name: 'MultiLineChart',
  props: {
    datetimeRange: {
      type: Array,
      default: () => {
        return [moment().subtract(1, 'hour'), moment()]
      }
    },
    // width: {
    //   type: Number,
    //   default: 850
    // },
    height: {
      type: Number,
      default: 400
    },
    chartId: {
      type: String,
      required: true
    },
    historyFunc: {
      type: Function,
      required: true
    },
    itemKey: {
      type: String,
      required: true
    },
    sourceId: {
      type: String,
      required: true
    },
    compareLastWeek: {
      type: Boolean,
      default: true
    },
    compareYesterday: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      spinning: false
    }
  },
  mounted () {
    this.render()
  },
  methods: {
    async render () {
      if (chart[this.chartId]) chart[this.chartId].destroy()
      chart[this.chartId] = new Chart({
        container: this.chartId,
        autoFit: true,
        height: this.height,
        appendPadding: [4, 4, 2, 2]
      })
      chart[this.chartId].scale({
        timestamp: {
          type: 'time',
          mask: 'MM-DD HH:mm'
        },
        value: {
          min: 0,
          nice: true,
          formatter: value => humanizeValue(value)
        }
      })
      chart[this.chartId].axis('timestamp', {
        tickLine: null,
        line: null,
        grid: null,
        label: {
          style: {
            fill: '#808080'
          }
        }
      })

      chart[this.chartId].axis('value', {
        grid: {
          line: {
            style: {
              stroke: '#cccccc',
              strokeOpacity: 0.8,
              lineWidth: 1,
              lineDash: [4]
            }
          }
        },
        label: {
          style: {
            fill: '#808080'
          }
        }
      })
      chart[this.chartId].tooltip({ showCrosshairs: true })
      chart[this.chartId].removeInteraction('tooltip')
      const datetimeRangeList = [this.datetimeRange]
      if (this.compareYesterday) {
        datetimeRangeList.push([
          moment(this.datetimeRange[0]).subtract(1, 'day'),
          moment(this.datetimeRange[1]).subtract(1, 'day')
        ])
      }
      if (this.compareLastWeek) {
        datetimeRangeList.push([
          moment(this.datetimeRange[0]).subtract(1, 'week'),
          moment(this.datetimeRange[1]).subtract(1, 'week')
        ])
      }
      const offset = 1 / datetimeRangeList.length
      this.spinning = true

      for (let i = 0; i < datetimeRangeList.length; i++) {
        const view = chart[this.chartId].createView({
          region: {
            start: {
              x: 0,
              y: offset * i
            },
            end: {
              x: 1,
              y: offset * i + offset
            }
          }
        })
        await this.historyFunc(this.sourceId, {
          key: this.itemKey,
          datetime_from: datetimeRangeList[i][0].format('YYYY-MM-DD HH:mm'),
          datetime_to: datetimeRangeList[i][1].format('YYYY-MM-DD HH:mm')
        }).then(res => {
          const data = res.data.data
          view.data(data)
          view.interaction('tooltip')
          view.interaction('sibling-tooltip')
          view.line().position('timestamp*value').color(colors[i])
          view.area().position('timestamp*value').color(`l(90) 0:${colors[i]} 1:${colors[i]}20`)
          if (data.length !== 0) {
            let max = data[0]
            let min = data[0]
            for (const datum of data) {
              if (datum.value > max.value) max = datum
              if (datum.value < min.value) min = datum
            }
            view.annotation().dataMarker({
              direction: 'downward',
              top: true,
              position: [max.timestamp, max.value],
              text: {
                content: `峰值: ${humanizeValue(max.value)}`
              }
            })
            if (max.timestamp !== min.timestamp) {
              view.annotation().dataMarker({
                top: true,
                position: [min.timestamp, min.value],
                text: {
                  content: `谷值: ${humanizeValue(min.value)}`
                }
              })
            }
          }
        }).catch(() => { this.spinning = false })
      }

      chart[this.chartId].render()
      this.spinning = false
    }
  },
  watch: {
    itemKey () {
      this.render()
    },
    datetimeRange () {
      this.render()
    },
    compareYesterday () {
      this.render()
    },
    compareLastWeek () {
      this.render()
    }
  }

}
</script>
